import React, { useEffect, useState } from 'react'
import injectSheet from 'react-jss'
import Button from 'pobble-components/dist/button'
import ButtonElement from 'pobble-components/dist/button-element'
import { Modal, ModalContent, ModalFooter } from 'pobble-components/dist/modal'
import { Image } from 'pobble-components/dist/image'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleLeft } from '@fortawesome/pro-solid-svg-icons/faAngleLeft'
import { faAngleRight } from '@fortawesome/pro-solid-svg-icons/faAngleRight'
import { PARENT_PRIMARY_CTA } from './parent-theme'

const getMediaFromWorkVersion = workVersion => {
  const media =
    (workVersion && workVersion.work && workVersion.work.media) || []
  return [...media].sort((a, b) => (a.order || 0) - (b.order || 0))
}

const getMediaImageUrl = media =>
  (media && (media.image_url || media.url || media.thumb_url)) || null

const getTypedBodyFromWorkVersion = workVersion => {
  if (!workVersion) return ''

  if (typeof workVersion.typed_body_cache === 'string') {
    return workVersion.typed_body_cache
  }

  if (typeof workVersion.typed_body === 'string') {
    return workVersion.typed_body
  }

  if (!workVersion.work || !workVersion.work.medium) return ''

  const details = workVersion.work.medium.details

  if (typeof details === 'string') return details
  if (details && typeof details.body === 'string') return details.body
  if (details && typeof details.value === 'string') return details.value

  return ''
}

const hasMeaningfulBody = html =>
  !!(
    html &&
    html
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim()
  )

export const ParentWritingWorkModal = injectSheet({
  content: {
    width: '100%',
    maxWidth: '560px',
    textAlign: 'center',
    '& h3': {
      margin: '0 0 16px',
      color: '#111827',
      font: 'var(--heading--)'
    }
  },
  preview: {
    margin: '0 auto',
    maxWidth: '100%',
    borderRadius: '12px',
    overflow: 'hidden',
    border: '1px solid #e5e7eb',
    background: '#f9fafb'
  },
  previewImage: {
    width: '100%',
    display: 'block',
    maxHeight: '420px',
    objectFit: 'contain'
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    marginTop: '16px'
  },
  paginationButton: {
    width: '40px',
    height: '40px',
    borderRadius: '100%',
    border: '1px solid #e5e7eb',
    background: '#fff',
    color: 'var(--purple)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:disabled': {
      opacity: 0.4,
      cursor: 'not-allowed'
    },
    '&:hover:not(:disabled)': {
      background: '#faf5ff',
      borderColor: 'var(--purple--)'
    }
  },
  paginationLabel: {
    margin: '0',
    minWidth: '72px',
    color: '#4b5563',
    font: 'var(--body--)',
    fontWeight: 'bold'
  },
  typedBody: {
    margin: '0',
    padding: '20px',
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
    background: '#fff',
    color: '#111827',
    font: 'var(--body)',
    lineHeight: 1.6,
    textAlign: 'left',
    maxHeight: '420px',
    overflowY: 'auto',
    '& p': {
      margin: '0 0 12px'
    },
    '& p:last-child': {
      marginBottom: 0
    },
    '& ul, & ol': {
      margin: '0 0 12px',
      paddingLeft: '24px'
    }
  },
  empty: {
    margin: '0',
    color: '#4b5563',
    font: 'var(--body)'
  },
  closeButton: PARENT_PRIMARY_CTA
})(function ParentWritingWorkModal({
  classes,
  parentWriting,
  isOpen,
  requestClose
}) {
  const [activeMediaIndex, setActiveMediaIndex] = useState(0)

  const workVersion =
    parentWriting && parentWriting.last_parent_writing_work_version
  const writingType = parentWriting && parentWriting.writing_type
  const mediaItems = getMediaFromWorkVersion(workVersion)
  const activeMedia = mediaItems[activeMediaIndex]
  const activeImageUrl = getMediaImageUrl(activeMedia)
  const typedBody = getTypedBodyFromWorkVersion(workVersion)
  const presentationTitle =
    parentWriting &&
    parentWriting.presentation &&
    parentWriting.presentation.title
  const parentWritingId = parentWriting && parentWriting.id

  useEffect(
    () => {
      if (isOpen) {
        setActiveMediaIndex(0)
      }
    },
    [isOpen, parentWritingId]
  )

  const hasUploadedWork =
    writingType === 'uploaded' && mediaItems.length > 0 && !!activeImageUrl
  const hasTypedWork = writingType === 'typed' && hasMeaningfulBody(typedBody)

  return (
    <Modal isOpen={isOpen} requestClose={requestClose} closeButton>
      <ModalContent className={classes.content}>
        <h3>
          {presentationTitle
            ? `Your writing for “${presentationTitle}”`
            : 'Your writing'}
        </h3>
        {hasUploadedWork && (
          <>
            <div className={classes.preview}>
              <Image
                className={classes.previewImage}
                src={activeImageUrl}
                alt={`Uploaded writing page ${activeMediaIndex + 1}`}
              />
            </div>
            {mediaItems.length > 1 && (
              <div className={classes.pagination}>
                <ButtonElement
                  className={classes.paginationButton}
                  disabled={activeMediaIndex === 0}
                  onClick={() =>
                    setActiveMediaIndex(index => Math.max(0, index - 1))
                  }>
                  <FontAwesomeIcon icon={faAngleLeft} />
                </ButtonElement>
                <p className={classes.paginationLabel}>
                  {activeMediaIndex + 1} of {mediaItems.length}
                </p>
                <ButtonElement
                  className={classes.paginationButton}
                  disabled={activeMediaIndex >= mediaItems.length - 1}
                  onClick={() =>
                    setActiveMediaIndex(index =>
                      Math.min(mediaItems.length - 1, index + 1)
                    )
                  }>
                  <FontAwesomeIcon icon={faAngleRight} />
                </ButtonElement>
              </div>
            )}
          </>
        )}
        {hasTypedWork && (
          <div
            className={classes.typedBody}
            dangerouslySetInnerHTML={{ __html: typedBody }}
          />
        )}
        {!hasUploadedWork &&
          !hasTypedWork && (
            <p className={classes.empty}>
              We couldn&apos;t find any saved work.
            </p>
          )}
      </ModalContent>
      <ModalFooter alignRight>
        <Button className={classes.closeButton} tall minw action={requestClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  )
})
